import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { logger } from '@musedrops/shared';
import type { Language } from '@musedrops/shared';
import { LanguageApi } from '../../services/languageApi';

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
type ChangeHandler<T> = (value: T) => void;

interface LanguageSelectorProps {
  value: string | null;
  onChange: ChangeHandler<string | null>;
  label?: string;
  required?: boolean;
  width?: 'full' | 'medium' | 'small';
  /** Show "All Languages" option that represents null */
  showAllOption?: boolean;
  /** Label for the "All" option (default: "All Languages") */
  allOptionLabel?: string;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  value,
  onChange,
  label = 'Language',
  required = false,
  width = 'full',
  showAllOption = false,
  allOptionLabel = 'All Languages',
}) => {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getWidth = () => {
    switch (width) {
      case 'small':
        return 200;
      case 'medium':
        return 300;
      case 'full':
      default:
        return '100%';
    }
  };

  useEffect(() => {
    const loadLanguages = async () => {
      try {
        setLoading(true);
        const allLanguages = await LanguageApi.getAll();
        setLanguages(allLanguages);
        setError('');
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load languages'
        );
        logger.error('Failed to load languages', { error: err });
      } finally {
        setLoading(false);
      }
    };

    loadLanguages();
  }, []);

  return (
    <Box sx={{ width: getWidth() }}>
      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <FormControl fullWidth required={required}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value ?? (showAllOption ? '__ALL__' : 'en')}
          onChange={e => {
            const newValue = e.target.value;
            // Sentinel value '__ALL__' represents null
            onChange(newValue === '__ALL__' ? null : newValue);
          }}
          label={label}
          disabled={loading}
        >
          {showAllOption && (
            <MenuItem value="__ALL__">
              <em>{allOptionLabel}</em>
            </MenuItem>
          )}
          {languages.map(language => (
            <MenuItem key={language.key} value={language.key}>
              {language.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};
